import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import * as L from 'leaflet';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { DropdownModule } from 'primeng/dropdown';
import { SliderModule } from 'primeng/slider';
import { DialogModule } from 'primeng/dialog';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { MessageService } from 'primeng/api';

import { ScholarshipsService } from '../../../services/scholarships/scholarships.service';
import { GetScholarshipRequest, ScholarshipType } from '../../../models/scholarships/getScholarshipRequest';
import { ScholarshipResponse } from '../../../models/scholarships/scholarshipResponse';
import { UniversityLogosMarqueeComponent } from '../university-logos-marquee/university-logos-marquee.component';
import { formatDeadline, parseDeadline } from '../../../pipe/deadlinePipe';

interface CountryStat {
  name: string;
  code: string;
  total: number;
  fullFunding: number;
  totalValueFormatted: string;
  intensity: number;
}

@Component({
  selector: 'app-scholarships',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LeafletModule,
    DropdownModule,
    SliderModule,
    DialogModule,
    OverlayPanelModule,
    UniversityLogosMarqueeComponent
  ],
  templateUrl: './scholarships.component.html',
  styleUrl: './scholarships.component.scss'
})
export class ScholarshipsComponent implements OnInit, OnDestroy {
  
  // Views Toggle State
  activeView: 'map' | 'calendar' = 'map';

  // Filter States
  locations = [
    { name: 'India', code: 'IN' },
    { name: 'United States', code: 'US' },
    { name: 'United Kingdom', code: 'UK' },
    { name: 'Canada', code: 'CA' },
    { name: 'Australia', code: 'AU' }
  ];
  selectedLocation: any;
  
  degreeLevel: string = 'All';
  eligibilityRange: number = 0;
  fundingType: string = 'All';

  // Map Data
  isLoading = false;
  allScholarshipsList: ScholarshipResponse[] = [];
  filteredScholarshipsList: ScholarshipResponse[] = [];
  countryStats = new Map<string, CountryStat>();

  private map!: L.Map;
  private geoJsonLayer: L.GeoJSON | null = null;
  private geoJsonData: any = null;

  mapOptions: L.MapOptions = {
    layers: [
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
        maxZoom: 18,
        minZoom: 2,
        noWrap: true,
        attribution: ''
      })
    ],
    zoom: 2,
    center: L.latLng(20, 0),
    zoomControl: true,
    dragging: true,
    touchZoom: true,
    scrollWheelZoom: true,
    doubleClickZoom: true,
    boxZoom: false,
    maxBounds: L.latLngBounds(L.latLng(-90, -180), L.latLng(90, 180)),
    maxBoundsViscosity: 1.0
  };

  layers: L.Layer[] = [];

  private countryAliases: { [key: string]: string } = {
    'usa': 'united states of america',
    'us': 'united states of america',
    'united states': 'united states of america',
    'uk': 'united kingdom',
    'uae': 'united arab emirates',
    'england': 'united kingdom'
  };

  // Calendar States
  currentMonthIndex: number = new Date().getMonth();
  currentYear: number = new Date().getFullYear();
  months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  calendarDays: any[] = [];
  upcomingDeadlines: ScholarshipResponse[] = [];
  selectedDate: Date | null = null;

  // Featured Scholarships States
  featuredScholarships: any[] = [];

  // Details Dialog States
  scholarshipDialogVisible = false;
  selectedScholarship: ScholarshipResponse | null = null;
  loadingScholarshipId?: number | null;
  studentMentors: any[] = [];
  selectedMentorToShare: any = null;
  isSharing = false;
  myScholarshipsList = signal<ScholarshipResponse[]>([]);

  constructor(
    private router: Router,
    private scholarshipsService: ScholarshipsService,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    this.getScholarships();
  }

  ngOnDestroy(): void {
    if (this.map) {
      try {
        this.map.off();
        this.map.remove();
      } catch (e) {
        console.warn('Leaflet cleanup error:', e);
      }
    }
  }

  setDegreeLevel(level: string) {
    this.degreeLevel = level;
  }

  setFundingType(type: string) {
    this.fundingType = type;
  }

  applyFilters() {
    let filtered = [...this.allScholarshipsList];

    // 1. Location
    if (this.selectedLocation) {
      const locName = this.selectedLocation.name.toLowerCase().trim();
      filtered = filtered.filter(s => {
        if (!s.country) return false;
        return s.country.some(c => {
          let normalized = c.toLowerCase().trim();
          normalized = this.countryAliases[normalized] || normalized;
          const target = this.countryAliases[locName] || locName;
          return normalized === target;
        });
      });
    }

    // 2. Degree Level
    if (this.degreeLevel !== 'All') {
      const targetLvl = this.degreeLevel.toLowerCase().trim();
      filtered = filtered.filter(s => {
        if (!s.level) return false;
        const lvl = s.level.toLowerCase().trim();
        if (targetLvl === 'under graduate' && (lvl === 'under graduate' || lvl === 'ug' || lvl === 'undergraduate')) return true;
        if (targetLvl === 'post graduate' && (lvl === 'post graduate' || lvl === 'pg' || lvl === 'postgraduate')) return true;
        if (targetLvl === 'phd' && (lvl === 'phd' || lvl === 'doctorate')) return true;
        return lvl === targetLvl;
      });
    }

    // 3. Academic Eligibility
    if (this.eligibilityRange > 0) {
      filtered = filtered.filter(s => {
        if (!s.eligibility) return true;
        const reqs = s.eligibility.academicRequirements;
        if (!reqs || typeof reqs !== 'string') return true;
        const match = reqs.match(/(\d+)%/);
        if (match) {
          const val = parseInt(match[1], 10);
          return this.eligibilityRange >= val;
        }
        return true;
      });
    }

    // 4. Funding Type
    if (this.fundingType !== 'All') {
      const targetFunding = this.fundingType.toLowerCase().trim();
      filtered = filtered.filter(s => {
        if (!s.fundingType) return false;
        let funding: string[] = [];
        if (Array.isArray(s.fundingType)) {
          funding = s.fundingType;
        } else if (typeof s.fundingType === 'string') {
          funding = [s.fundingType];
        }
        return funding.some(f => f.toLowerCase().trim().includes(targetFunding));
      });
    }

    this.filteredScholarshipsList = filtered;
    this.selectedDate = null; // Reset calendar selection on filter change
    this.processMapData();
    this.generateCalendarGrid();

    // Zoom to country if selected
    if (this.selectedLocation) {
      this.zoomToCountry(this.selectedLocation.name);
    }
  }

  resetFilters() {
    this.selectedLocation = null;
    this.degreeLevel = 'All';
    this.eligibilityRange = 0;
    this.fundingType = 'All';
    this.applyFilters();

    // Zoom back to default world view and close popups
    if (this.map) {
      this.map.setView([20, 0], 2);
      this.map.closePopup();
    }
  }

  zoomToCountry(countryName: string) {
    if (!this.map || !this.geoJsonLayer) return;
    
    const target = (this.countryAliases[countryName.toLowerCase().trim()] || countryName.toLowerCase().trim());

    let foundLayer: any = null;
    this.geoJsonLayer.eachLayer((layer: any) => {
      const props = layer.feature?.properties;
      if (props) {
        let name = props.ADMIN || props.name || props.NAME || '';
        let normalized = name.toLowerCase().trim();
        normalized = this.countryAliases[normalized] || normalized;
        if (normalized === target) {
          foundLayer = layer;
        }
      }
    });

    if (foundLayer) {
      try {
        const bounds = foundLayer.getBounds();
        this.map.fitBounds(bounds, { maxZoom: 4, animate: true, duration: 1.0 });
        
        setTimeout(() => {
          foundLayer.openPopup();
        }, 800);
      } catch (e) {
        console.warn('Error zooming to country:', e);
      }
    }
  }

  setView(view: 'map' | 'calendar') {
    this.activeView = view;
    if (view === 'map' && this.map) {
      setTimeout(() => {
        this.map.invalidateSize();
      }, 150);
    }
  }

  prevMonth() {
    if (this.currentMonthIndex === 0) {
      this.currentMonthIndex = 11;
      this.currentYear--;
    } else {
      this.currentMonthIndex--;
    }
    this.selectedDate = null; // Reset selection on month navigation
    this.generateCalendarGrid();
  }

  nextMonth() {
    if (this.currentMonthIndex === 11) {
      this.currentMonthIndex = 0;
      this.currentYear++;
    } else {
      this.currentMonthIndex++;
    }
    this.selectedDate = null; // Reset selection on month navigation
    this.generateCalendarGrid();
  }

  getScholarships() {
    this.isLoading = true;
    const req: GetScholarshipRequest = {
      scholarshipName: '',
      scholarshipType: ScholarshipType.Scholarship,
      stream: null,
      level: null,
    };

    this.scholarshipsService.getScholarships(req).subscribe({
      next: (response) => {
        this.allScholarshipsList = response ? response.result : [];
        this.filteredScholarshipsList = [...this.allScholarshipsList];
        this.processMapData();
        this.loadGeoJson();
        this.generateCalendarGrid();
        this.selectFeaturedScholarships();
        this.getMyScholarshipsList();
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error Fetching Scholarships:', err);
      }
    });
  }

  loadGeoJson() {
    const geoJsonUrl = 'https://raw.githubusercontent.com/johan/world.geo.json/master/countries.geo.json';
    fetch(geoJsonUrl)
      .then(res => res.json())
      .then(data => {
        this.geoJsonData = data;
        this.renderGeoJson();
      })
      .catch(err => {
        console.error('Error loading GeoJSON:', err);
      });
  }

  processMapData() {
    if (!this.filteredScholarshipsList || this.filteredScholarshipsList.length === 0) {
      this.countryStats.clear();
      this.renderGeoJson();
      return;
    }
    this.countryStats.clear();
    
    this.filteredScholarshipsList.forEach(s => {
      const countries = Array.isArray(s.country) ? s.country : [s.country];
      countries.forEach(cName => {
        if (!cName) return;
        let normalized = cName.toLowerCase().trim();
        normalized = this.countryAliases[normalized] || normalized;
        
        let stat = this.countryStats.get(normalized);
        if (!stat) {
          stat = {
            name: cName,
            code: '',
            total: 0,
            fullFunding: 0,
            totalValueFormatted: '$0',
            intensity: 0
          };
          this.countryStats.set(normalized, stat);
        }
        
        stat.total++;
        if (s.fullFunding) stat.fullFunding++;
      });
    });

    const statsArray = Array.from(this.countryStats.values());
    const maxTotal = statsArray.length > 0 ? Math.max(...statsArray.map(s => s.total)) : 1;
    
    this.countryStats.forEach(stat => {
      stat.intensity = stat.total / maxTotal;
    });

    this.renderGeoJson();
  }

  renderGeoJson() {
    if (!this.geoJsonData) return;
    this.geoJsonLayer = L.geoJSON(this.geoJsonData, {
      style: (feature) => this.getCountryStyle(feature),
      onEachFeature: (feature, layer) => this.onEachFeature(feature, layer)
    });
    this.layers = [this.geoJsonLayer];
  }

  getCountryStyle(feature: any): L.PathOptions {
    const props = feature.properties;
    let countryName = props.ADMIN || props.name || props.NAME || '';
    let normalized = countryName.toLowerCase().trim();
    normalized = this.countryAliases[normalized] || normalized;
    
    const stat = this.countryStats.get(normalized);
    const intensity = stat ? stat.intensity : 0;

    return {
      fillColor: '#6062b0',
      weight: 1,
      opacity: 1,
      color: '#ffffff',
      fillOpacity: intensity > 0 ? 0.9 : 0.6
    };
  }

  onEachFeature(feature: any, layer: L.Layer) {
    const props = feature.properties;
    let countryName = props.ADMIN || props.name || props.NAME || '';
    let normalized = countryName.toLowerCase().trim();
    normalized = this.countryAliases[normalized] || normalized;
    
    const stat = this.countryStats.get(normalized);

    if (stat && stat.total > 0) {
      const popupContent = `
        <div class="custom-map-popup" style="width: 250px; font-family: 'Circular Std', sans-serif;">
          <div style="padding: 16px;">
            <div style="display: flex; align-items: center; margin-bottom: 16px;">
              <div style="width: 24px; height: 24px; border-radius: 50%; overflow: hidden; margin-right: 12px; background: #eee; display: flex; align-items: center; justify-content: center;">
                <img src="https://flagcdn.com/w40/${this.guessCountryCode(countryName)}.png" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.style.display='none'">
              </div>
              <h3 style="margin: 0; font-size: 18px; color: #0b117b; font-weight: bold;">${stat.name}</h3>
            </div>
            
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <i class="pi pi-list" style="color: #6062b0; font-size: 14px;"></i>
                <span style="font-size: 14px; color: #6062b0;">Active Scholarships</span>
              </div>
              <span style="font-weight: bold; font-size: 15px; color: #000;">${stat.total}</span>
            </div>

            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <i class="pi pi-building" style="color: #6062b0; font-size: 14px;"></i>
                <span style="font-size: 14px; color: #6062b0;">Full Funding</span>
              </div>
              <span style="font-weight: bold; font-size: 15px; color: #000;">${stat.fullFunding}</span>
            </div>
            
            <button id="explore-${stat.name.replace(/\s+/g, '-')}" 
                    style="width: 100%; padding: 12px; border-radius: 6px; border: none; background-color: #0b117b; color: white; font-weight: bold; font-size: 14px; cursor: pointer;">
              Browse Opportunities
            </button>
          </div>
        </div>
      `;

      layer.bindPopup(popupContent, {
        maxWidth: 260,
        className: 'elegant-popup',
        autoPanPadding: [30, 30]
      });

      let hoverTimeout: any = null;

      layer.on('popupopen', (event: any) => {
        const btn = document.getElementById(`explore-${stat.name.replace(/\s+/g, '-')}`);
        if (btn) {
          btn.onclick = () => this.exploreRegion(stat.name);
        }
        const popupNode = event.popup.getElement();
        if (popupNode) {
          popupNode.addEventListener('mouseenter', () => {
            if (hoverTimeout) {
              clearTimeout(hoverTimeout);
              hoverTimeout = null;
            }
          });
          popupNode.addEventListener('mouseleave', () => {
            hoverTimeout = setTimeout(() => {
              layer.closePopup();
            }, 300);
          });
        }
      });

      layer.on('mouseover', () => {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          hoverTimeout = null;
        }
        if (layer instanceof L.Path) {
          layer.setStyle({ fillOpacity: 1, color: '#d3de32', weight: 2 });
        }
        
        const normName = stat.name.toLowerCase().trim();
        const isUS = normName === 'usa' || normName === 'us' || normName === 'united states' || normName === 'united states of america';
        if (isUS) {
          layer.openPopup(L.latLng(37, -95));
        } else {
          layer.openPopup();
        }
      });

      layer.on('mouseout', () => {
        if (this.geoJsonLayer && layer instanceof L.Path) {
          this.geoJsonLayer.resetStyle(layer);
        }
        hoverTimeout = setTimeout(() => {
          layer.closePopup();
        }, 300);
      });
    }
  }

  guessCountryCode(name: string): string {
    const map: any = {
      'india': 'in', 'united states': 'us', 'united states of america': 'us',
      'united kingdom': 'gb', 'canada': 'ca', 'australia': 'au',
      'germany': 'de', 'france': 'fr', 'italy': 'it', 'spain': 'es'
    };
    return map[name.toLowerCase()] || 'un';
  }

  onMapReady(map: L.Map) {
    this.map = map;
    setTimeout(() => {
      map.invalidateSize();
      if (this.geoJsonData) {
        this.processMapData();
      }
    }, 200);
  }

  exploreRegion(regionName: string) {
    this.router.navigate(['/portal/institute-search'], { queryParams: { country: regionName, tab: 'scholarships' } });
  }

  // Calendar Engine
  parseScholarshipDeadline(s: ScholarshipResponse): { monthIndex: number; day: number | null } | null {
    if (!s.deadLine) return null;

    const deadline = s.deadLine;
    let monthStr = '';
    let exactDateStr: string | null = null;

    if (typeof deadline === 'object' && deadline !== null) {
      monthStr = (deadline.month || '').toLowerCase();
      exactDateStr = deadline.exactDate || null;
    } else {
      monthStr = String(deadline).toLowerCase();
      exactDateStr = String(deadline);
    }

    if (!monthStr) return null;

    const monthMap: { [key: string]: number } = {
      'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
      'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11,
      'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'jun': 5, 'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
    };

    let sMonthIndex = -1;
    for (const key in monthMap) {
      if (monthStr.includes(key)) {
        sMonthIndex = monthMap[key];
        break;
      }
    }

    if (sMonthIndex === -1) return null;

    let day: number | null = null;
    if (exactDateStr) {
      const dateObj = new Date(exactDateStr);
      if (!isNaN(dateObj.getTime())) {
        day = dateObj.getDate();
      } else {
        const dayMatch = exactDateStr.match(/\b\d{1,2}\b/);
        day = dayMatch ? parseInt(dayMatch[0], 10) : null;
      }
    }

    return { monthIndex: sMonthIndex, day };
  }

  getScholarshipsForDate(date: Date): ScholarshipResponse[] {
    const month = date.getMonth();
    const day = date.getDate();

    return this.filteredScholarshipsList.filter(s => {
      const parsed = this.parseScholarshipDeadline(s);
      if (!parsed) return false;

      if (parsed.day !== null) {
        return parsed.monthIndex === month && parsed.day === day;
      } else {
        // Deterministically distribute across days in month to simulate populated calendar
        const detDay = (s.id % 15) * 2 + 1;
        return parsed.monthIndex === month && detDay === day;
      }
    });
  }

  generateCalendarGrid() {
    const year = this.currentYear;
    const month = this.currentMonthIndex;

    const firstDay = new Date(year, month, 1);
    const startDayOfWeek = firstDay.getDay();
    const mondayOffset = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

    const totalDays = new Date(year, month + 1, 0).getDate();
    const prevMonthLastDate = new Date(year, month, 0).getDate();

    const days: any[] = [];

    // 1. Prev month trailing days
    for (let i = mondayOffset - 1; i >= 0; i--) {
      const dayNum = prevMonthLastDate - i;
      const date = new Date(year, month - 1, dayNum);
      days.push({
        dayNumber: dayNum,
        isCurrentMonth: false,
        date: date,
        scholarships: this.getScholarshipsForDate(date)
      });
    }

    // 2. Current month days
    for (let i = 1; i <= totalDays; i++) {
      const date = new Date(year, month, i);
      days.push({
        dayNumber: i,
        isCurrentMonth: true,
        date: date,
        scholarships: this.getScholarshipsForDate(date)
      });
    }

    // 3. Next month leading days
    const totalCells = days.length > 35 ? 42 : 35;
    const remaining = totalCells - days.length;
    for (let i = 1; i <= remaining; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        dayNumber: i,
        isCurrentMonth: false,
        date: date,
        scholarships: this.getScholarshipsForDate(date)
      });
    }

    this.calendarDays = days;
    this.updateUpcomingDeadlines();
  }

  updateUpcomingDeadlines() {
    if (this.selectedDate) {
      // Ensure the selected date still exists in the calendar data
      const selectedDayObj = this.calendarDays.find(d => 
        d.date.getFullYear() === this.selectedDate!.getFullYear() &&
        d.date.getMonth() === this.selectedDate!.getMonth() &&
        d.date.getDate() === this.selectedDate!.getDate()
      );
      if (selectedDayObj) {
        this.upcomingDeadlines = selectedDayObj.scholarships;
        return;
      }
    }

    this.upcomingDeadlines = this.filteredScholarshipsList.filter(s => {
      const parsed = this.parseScholarshipDeadline(s);
      return parsed !== null && parsed.monthIndex === this.currentMonthIndex;
    });
  }

  selectDate(day: any): void {
    if (day && day.scholarships && day.scholarships.length > 0) {
      this.selectedDate = day.date;
      this.upcomingDeadlines = day.scholarships;
    }
  }

  clearSelectedDate(): void {
    this.selectedDate = null;
    this.updateUpcomingDeadlines();
  }

  isDateSelected(date: Date): boolean {
    if (!this.selectedDate) return false;
    return date.getFullYear() === this.selectedDate.getFullYear() &&
           date.getMonth() === this.selectedDate.getMonth() &&
           date.getDate() === this.selectedDate.getDate();
  }

  // Scholarship Dialog Handlers
  openScholarshipDetails(scholarship: ScholarshipResponse): void {
    if (this.loadingScholarshipId) return;
    this.loadingScholarshipId = scholarship.id;
    this.scholarshipsService.getScholarshipById(scholarship.id).subscribe({
      next: (detailedScholarship) => {
        detailedScholarship.isMyScholarship = scholarship.isMyScholarship;
        this.selectedScholarship = detailedScholarship;
        this.scholarshipDialogVisible = true;
        this.loadingScholarshipId = null;
      },
      error: (err) => {
        this.loadingScholarshipId = null;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: `Error Fetching Scholarship Details: ${err}` });
      }
    });
  }

  closeScholarshipDialog(): void {
    this.scholarshipDialogVisible = false;
    this.selectedScholarship = null;
  }

  getAcademicMainText(reqs: any): string {
    if (!reqs || typeof reqs !== 'string') return "Bachelor's degree";
    const clean = reqs.trim();
    if (clean.includes('\n')) return clean.split('\n')[0].trim();
    if (clean.includes(';')) return clean.split(';')[0].trim();
    return clean;
  }

  getAcademicSubText(reqs: any): string | null {
    if (!reqs || typeof reqs !== 'string') {
      if (this.selectedScholarship?.id === 1 || (this.selectedScholarship?.name && this.selectedScholarship.name.includes('Fulbright'))) {
        return "3+ years work experience";
      }
      return null;
    }
    const clean = reqs.trim();
    if (clean.includes('\n')) return clean.split('\n')[1].trim();
    if (clean.includes(';')) return clean.split(';')[1].trim();
    if (this.selectedScholarship?.id === 1 || (this.selectedScholarship?.name && this.selectedScholarship.name.includes('Fulbright'))) {
      return "3+ years work experience";
    }
    return null;
  }

  getCountryDisplayName(country: any): string {
    if (!country) return 'Global';
    let name: any = Array.isArray(country) ? country[0] : country;
    if (!name) return 'Global';
    if (typeof name !== 'string') {
      name = name.name || name.toString() || '';
    }
    const lower = name.toLowerCase().trim();
    if (lower === 'united states' || lower === 'united states of america' || lower === 'us') {
      return 'USA';
    }
    if (lower === 'united kingdom' || lower === 'uk') {
      return 'UK';
    }
    return name;
  }

  getLevelDisplayName(level: any): string {
    if (!level || typeof level !== 'string') return 'Any';
    const lvl = level.toLowerCase().trim();
    if (lvl === 'post graduate' || lvl === 'pg' || lvl === 'postgraduate') return 'PG (Postgraduate)';
    if (lvl === 'under graduate' || lvl === 'ug' || lvl === 'undergraduate') return 'UG (Undergraduate)';
    if (lvl === 'phd' || lvl === 'doctorate') return 'PhD (Doctorate)';
    return level;
  }

  isCovered(type: 'tuition' | 'living' | 'travel' | 'health'): boolean {
    if (!this.selectedScholarship) return false;
    if (this.selectedScholarship.fullFunding) return true;
    
    let funding: any[] = [];
    if (Array.isArray(this.selectedScholarship.fundingType)) {
      funding = this.selectedScholarship.fundingType;
    } else if (typeof this.selectedScholarship.fundingType === 'string') {
      funding = [this.selectedScholarship.fundingType];
    }
    
    const fundingLower = funding
      .filter(f => typeof f === 'string')
      .map(f => f.toLowerCase().trim());
    
    if (type === 'tuition') {
      return fundingLower.some(f => f.includes('tuition') || f.includes('tution') || f.includes('full') || f.includes('stipend') || f.includes('fee'));
    }
    if (type === 'living') {
      return fundingLower.some(f => f.includes('living') || f.includes('full') || f.includes('stipend') || f.includes('accommodation'));
    }
    if (type === 'travel') {
      return fundingLower.some(f => f.includes('travel') || f.includes('full') || f.includes('airfare'));
    }
    if (type === 'health') {
      return fundingLower.some(f => f.includes('health') || f.includes('full') || f.includes('insurance') || f.includes('medical'));
    }
    return false;
  }

  getCoverageType(): string {
    if (!this.selectedScholarship) return '';
    if (this.selectedScholarship.fullFunding) return 'Full • 100%';
    
    let coveredCount = 0;
    if (this.isCovered('tuition')) coveredCount++;
    if (this.isCovered('living')) coveredCount++;
    if (this.isCovered('travel')) coveredCount++;
    if (this.isCovered('health')) coveredCount++;
    
    if (coveredCount === 0) return 'Partial';
    return `Partial • ${coveredCount * 25}%`;
  }

  getDeadlineDisplay(deadLine: any): string {
    if (!deadLine) return 'Varies';
    // GP-CB-05: when there is an exact deadline, show it in the fixed format ("March 31, 2026") instead of month + date.
    if (typeof deadLine !== 'string') {
      const exact = parseDeadline(deadLine.exactDate || deadLine.ExactDate || deadLine.exactdate);
      if (exact) return formatDeadline(exact);
    }

    let displayStr = '';
    if (typeof deadLine === 'string') {
      displayStr = deadLine;
    } else {
      const monthVal = deadLine.month || deadLine.Month;
      const dateVal = deadLine.exactDate || deadLine.ExactDate || deadLine.exactdate || '';
      
      if (!monthVal) return 'Varies';
      const month = this.formatMonth(monthVal);
      const date = dateVal ? ` ${dateVal}` : '';
      displayStr = `${month}${date}`;
    }
    
    let clean = displayStr.split('T')[0];
    clean = clean.replace(/\b\d{1,2}:\d{2}(?::\d{2})?\s*(?:AM|PM|am|pm)?/gi, '');
    clean = clean.replace(/\b(?:AM|PM|am|pm)\b/gi, '');
    clean = clean.replace(/\s+/g, ' ').trim();
    if (clean.endsWith(',')) {
      clean = clean.slice(0, -1).trim();
    }
    return clean;
  }

  getCountryPillName(country: any): string {
    if (!country) return 'Global';
    let name: any = Array.isArray(country) ? country[0] : country;
    if (!name) return 'Global';
    if (typeof name !== 'string') {
      name = name.name || name.toString() || 'Global';
    }
    const lower = name.toLowerCase().trim();
    if (lower === 'usa' || lower === 'us' || lower === 'united states' || lower === 'united states of america') {
      return 'United States';
    }
    if (lower === 'uk' || lower === 'united kingdom') {
      return 'United Kingdom';
    }
    return name;
  }

  getStreamDisplayName(stream: any): string {
    if (!stream) return 'Varies';
    if (Array.isArray(stream)) {
      return stream.join(', ');
    }
    if (typeof stream !== 'string') {
      return stream.toString() || 'Varies';
    }
    return stream;
  }

  shareScholarshipWithMentor() {
    if (!this.selectedMentorToShare || !this.selectedScholarship) return;
    this.isSharing = true;
    setTimeout(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Shared',
        detail: `Scholarship shared with ${this.selectedMentorToShare.label}`
      });
      this.isSharing = false;
      this.selectedMentorToShare = null;
    }, 1000);
  }

  navigateToMentors() {
    this.closeScholarshipDialog();
    this.router.navigate(['/portal/mentor-me']);
  }

  toggleMyScholarship(id: number) {
    this.loadingScholarshipId = id;
    const isSaved = this.myScholarshipsList().some(s => s.id === id);
    if (!isSaved) {
      this.scholarshipsService.addMyScholarship(id).subscribe({
        next: () => {
          this.loadingScholarshipId = null;
          this.getMyScholarshipsList();
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Scholarship saved to your list' });
        },
        error: () => {
          this.loadingScholarshipId = null;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save scholarship' });
        }
      });
    } else {
      this.scholarshipsService.deleteMyScholarShip(id).subscribe({
        next: () => {
          this.loadingScholarshipId = null;
          this.getMyScholarshipsList();
          this.messageService.add({ severity: 'success', summary: 'Removed', detail: 'Scholarship removed from your list' });
        },
        error: () => {
          this.loadingScholarshipId = null;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to remove scholarship' });
        }
      });
    }
  }

  getMyScholarshipsList() {
    this.scholarshipsService.getMyScholarships().subscribe({
      next: (response) => {
        const myScholarshipsIds = response.result.map(s => s.scholarship.id);
        this.myScholarshipsList.set(response.result.map(item => ({
          ...item.scholarship,
          isMyScholarship: true
        })));
        this.allScholarshipsList = this.allScholarshipsList.map(item => ({
          ...item,
          isMyScholarship: myScholarshipsIds.includes(item.id)
        }));
        this.featuredScholarships = this.featuredScholarships.map(card => ({
          ...card,
          isMyScholarship: myScholarshipsIds.includes(card.id)
        }));
        if (this.selectedScholarship) {
          this.selectedScholarship.isMyScholarship = myScholarshipsIds.includes(this.selectedScholarship.id);
        }
      },
      error: (err) => {
        console.warn('Error fetching my scholarships:', err);
      }
    });
  }

  getFormattedAmount(item: any): string {
    if (!item) return 'Varies';

    // 1. Prioritize USD (currency === 0) from the amount array
    let amtStr = '';
    const amounts = item.scholarship?.amount || [];
    const usdAmountObj = amounts.find((a: any) => a.currency === 0);
    
    if (usdAmountObj && usdAmountObj.amount) {
      amtStr = usdAmountObj.amount.toString().trim();
    } else if (amounts.length > 0 && amounts[0].amount) {
      amtStr = amounts[0].amount.toString().trim();
    } else if (item.scholarshipAmount) {
      amtStr = item.scholarshipAmount.toString().trim();
    } else if (item.scholarship?.exactNumbersUnavailable) {
      amtStr = item.scholarship.exactNumbersUnavailable.toString().trim();
    }

    if (!amtStr) {
      if (item.fullFunding) return 'Full Funding';
      return 'Varies';
    }

    const lowerAmt = amtStr.toLowerCase();
    if (lowerAmt === 'varies' || lowerAmt === 'variable' || lowerAmt === 'varies/variable' || lowerAmt === 'varies / variable') {
      if (item.fullFunding) return 'Full Funding';
      return 'Varies';
    }

    // Strip currency symbols and commas to check if it's purely numeric
    const numericClean = amtStr.replace(/[$\u00A3\u20AC\u20B9\u20A8,\s]/g, '');
    if (/^\d+(\.\d+)?$/.test(numericClean)) {
      const num = parseFloat(numericClean);
      // Prepend original currency symbol if it was present
      if (amtStr.includes('£') || amtStr.includes('\u00A3')) {
        return '£' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
      }
      if (amtStr.includes('€') || amtStr.includes('\u20AC')) {
        return '€' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
      }
      if (amtStr.includes('₹') || amtStr.includes('\u20B9')) {
        return '₹' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
      }
      return '$' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
    }

    if (/^[\$\£\€\¥\₹]/.test(amtStr)) {
      return amtStr;
    }

    return amtStr;
  }

  getScholarshipImage(item: any): string | null {
    if (!item) return null;
    if (item.image && typeof item.image === 'string') {
      const img = item.image.trim();
      const isUrl = img.startsWith('http://') || img.startsWith('https://');
      if (isUrl) {
        return img;
      }
    }
    return null;
  }

  getCountryFlag(country: string | string[] | null | undefined): string | null {
    if (!country) return null;
    const name = Array.isArray(country) ? country[0] : country;
    if (!name) return null;

    if (name.toLowerCase() === 'global') {
      return null;
    }

    const common: { [key: string]: string } = {
      'usa': 'us', 'united states': 'us', 'united states of america': 'us',
      'uk': 'gb', 'united kingdom': 'gb', 'india': 'in', 'canada': 'ca', 'australia': 'au',
      'germany': 'de', 'france': 'fr', 'china': 'cn', 'japan': 'jp'
    };
    const code = common[name.toLowerCase()];
    return code ? `https://flagcdn.com/w40/${code}.png` : null;
  }

  getDeadlineMonthName(s: ScholarshipResponse | null): string {
    if (!s || !s.deadLine) return 'Varies';
    const deadline = s.deadLine;
    if (typeof deadline === 'object' && deadline !== null) {
      return this.formatMonth(deadline.month);
    }
    return this.formatMonth(String(deadline));
  }

  selectFeaturedScholarships() {
    const list = this.allScholarshipsList || [];
    const featured: any[] = [];

    // 1. Inlaks Scholarship
    const inlaks = list.find(s => s.name?.toLowerCase().includes('inlaks'));
    if (inlaks) {
      featured.push({
        id: inlaks.id,
        name: inlaks.name,
        university: inlaks.universityOrOrganisationName || 'Inlaks Foundation',
        amount: this.getFormattedAmount(inlaks),
        deadline: inlaks.deadLine ? `Deadline: ${this.getDeadlineMonthName(inlaks)}` : 'Deadline: 15 April, 2026',
        link: inlaks.applicationLink || 'https://www.inlaksfoundation.org',
        isMyScholarship: inlaks.isMyScholarship || false
      });
    } else {
      featured.push({
        id: 419,
        name: 'Inlaks Scholarships',
        university: 'Inlaks Foundation',
        amount: 'Full Funding',
        deadline: 'Deadline: 15 April, 2026',
        link: 'https://www.inlaksfoundation.org',
        isMyScholarship: false
      });
    }

    // 2. Knight-Hennessy Scholars
    const kh = list.find(s => s.name?.toLowerCase().includes('knight-hennessy') || s.name?.toLowerCase().includes('knight hennessy'));
    if (kh) {
      featured.push({
        id: kh.id,
        name: kh.name,
        university: kh.universityOrOrganisationName || 'Stanford University',
        amount: this.getFormattedAmount(kh),
        deadline: kh.deadLine ? `Deadline: ${this.getDeadlineMonthName(kh)}` : 'Deadline: 09 October, 2026',
        link: kh.applicationLink || 'https://knight-hennessy.stanford.edu',
        isMyScholarship: kh.isMyScholarship || false
      });
    } else {
      featured.push({
        id: 429,
        name: 'Knight-Hennessy Scholars (Stanford)',
        university: 'Stanford University',
        amount: '$300,000',
        deadline: 'Deadline: 09 October, 2026',
        link: 'https://knight-hennessy.stanford.edu',
        isMyScholarship: false
      });
    }

    // 3. Gonzalo and Maria Garcia Latin America Scholarship for Women
    const gonzalo = list.find(s => s.name?.toLowerCase().includes('gonzalo'));
    if (gonzalo) {
      const gLink = gonzalo.applicationLink?.trim() || '';
      featured.push({
        id: gonzalo.id,
        name: gonzalo.name,
        university: gonzalo.universityOrOrganisationName || 'London Business School',
        amount: this.getFormattedAmount(gonzalo),
        deadline: (gonzalo.deadLine && (gonzalo.deadLine.month || gonzalo.deadLine.exactDate)) 
          ? `Deadline: ${this.getDeadlineMonthName(gonzalo)}` 
          : 'Deadline: Varies',
        link: (gLink.startsWith('http://') || gLink.startsWith('https://')) ? gLink : 'https://www.london.edu',
        isMyScholarship: gonzalo.isMyScholarship || false
      });
    } else {
      featured.push({
        id: 568,
        name: 'Gonzalo and Maria Garcia Latin America Scholarship for Women',
        university: 'London Business School',
        amount: '$100,000',
        deadline: 'Deadline: Varies',
        link: 'https://www.london.edu',
        isMyScholarship: false
      });
    }

    this.featuredScholarships = featured;
  }

  navigateToAllScholarships() {
    this.router.navigate(['/portal/institute-search'], { queryParams: { tab: 'scholarships' } });
  }

  bookClarityCall() {
    this.router.navigate(['/portal/peer']);
  }

  formatMonth(month: string | null | undefined): string {
    if (!month) return 'Varies';
    const m = month.toLowerCase().trim();
    const map: { [key: string]: string } = {
      'jan': 'January', 'january': 'January',
      'feb': 'February', 'february': 'February',
      'mar': 'March', 'march': 'March',
      'apr': 'April', 'april': 'April',
      'may': 'May',
      'jun': 'June', 'june': 'June',
      'jul': 'July', 'july': 'July',
      'aug': 'August', 'august': 'August',
      'sep': 'September', 'september': 'September',
      'oct': 'October', 'october': 'October',
      'nov': 'November', 'november': 'November',
      'dec': 'December', 'december': 'December'
    };
    return map[m] || (month.charAt(0).toUpperCase() + month.slice(1).toLowerCase());
  }

  scrollToScholarshipFinder() {
    const element = document.getElementById('scholarship-finder');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
